import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { update as updateUser } from 'redux/modules/auth';
import {
  hideActionToast,
  showActionToast,
} from 'components/ActionToast/actionToastBus';
import { trackEvent } from 'helpers/Analytics';
import { upsertEntry, withoutEntries } from 'helpers/careerEntries';

// How long Undo is offered before a delete is sent to the API.
export const UNDO_TIMEOUT = 5000;

const INCLUDES = [
  'skills:accepted(null)',
  'sectors',
  'tools',
  'education',
  'language',
  'diversity',
  'workexperience',
];

/**
 * Saves one work experience or education entry at a time. The API takes the
 * whole array, so each save starts from the latest `entries` the page has.
 * Education and certifications share user.education, so they share one of
 * these.
 *
 * Deletes hide the entry straight away and wait UNDO_TIMEOUT before saving,
 * so Undo needs no restore endpoint. Saving anything else in the meantime,
 * or leaving the page, sends the delete along with it.
 *
 * `meta` on each call is { lastUpdated, eventName }: the auth lastUpdated key
 * that drives a section's saved tick, and the analytics event.
 */
const useCareerEntries = ({ user, entries, field }) => {
  const dispatch = useDispatch();
  const [removed, setRemoved] = useState([]);
  const [saving, setSaving] = useState(false);
  const latest = useRef();
  latest.current = { user, entries, removed };
  const timer = useRef(null);
  const removedMeta = useRef(null);
  const mounted = useRef(true);

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const persist = useCallback(
    (next, { lastUpdated, eventName }) =>
      dispatch(
        updateUser(
          latest.current.user,
          { [field]: next, portfolioUpdate: true },
          false,
          INCLUDES,
          '',
          lastUpdated
        )
      ).then((result) => {
        trackEvent(eventName);
        return result;
      }),
    [dispatch, field]
  );

  const forget = (gone) => {
    if (mounted.current) {
      setRemoved((current) => withoutEntries(current, gone));
    }
  };

  const commitRemoved = () => {
    clearTimer();
    const { entries: current, removed: gone } = latest.current;
    if (gone.length === 0) {
      return;
    }
    persist(withoutEntries(current, gone), removedMeta.current).then(
      () => forget(gone),
      () => {
        forget(gone);
        showActionToast({
          message: 'We couldn’t delete that. Please try again.',
        });
      }
    );
  };
  const commitRef = useRef(commitRemoved);
  commitRef.current = commitRemoved;

  useEffect(
    () => () => {
      mounted.current = false;
      if (timer.current) {
        commitRef.current();
      }
    },
    []
  );

  /** Adds `entry`, or replaces `original` with it. Rejects with the API error. */
  const saveEntry = useCallback(
    (original, entry, meta) => {
      const { entries: current, removed: gone } = latest.current;
      if (gone.length > 0) {
        clearTimer();
        hideActionToast();
      }
      setSaving(true);
      return persist(
        upsertEntry(withoutEntries(current, gone), original, entry),
        meta
      )
        .then(
          () => forget(gone),
          (error) => {
            // The deletes didn't go either; send them on their own.
            if (gone.length > 0) {
              timer.current = setTimeout(() => commitRef.current(), 0);
            }
            throw error;
          }
        )
        .finally(() => {
          if (mounted.current) {
            setSaving(false);
          }
        });
    },
    [persist]
  );

  const deleteEntry = useCallback((entry, message, meta) => {
    setRemoved((current) => [...current, entry]);
    removedMeta.current = meta;
    clearTimer();
    timer.current = setTimeout(() => commitRef.current(), UNDO_TIMEOUT);
    showActionToast({
      message,
      actionLabel: 'Undo',
      timeout: UNDO_TIMEOUT,
      onAction: () => {
        const rest = withoutEntries(latest.current.removed, [entry]);
        setRemoved(rest);
        if (rest.length === 0) {
          clearTimer();
        }
      },
    });
  }, []);

  const visibleEntries = useMemo(
    () => withoutEntries(entries, removed),
    [entries, removed]
  );

  return {
    visibleEntries,
    saving,
    saveEntry,
    deleteEntry,
  };
};

/**
 * Which entry's dialog is open: `entry` is null when adding. The key stays
 * while the dialog animates out, and changes so each open starts fresh.
 * `addRequest` changes when another section asks this one to open Add.
 */
export const useEntryDialog = (addRequest) => {
  const [dialog, setDialog] = useState({ open: false, entry: null, key: 0 });

  const open = useCallback(
    (entry = null) =>
      setDialog((current) => ({ open: true, entry, key: current.key + 1 })),
    []
  );
  const close = useCallback(
    () => setDialog((current) => ({ ...current, open: false })),
    []
  );

  useEffect(() => {
    if (addRequest) {
      open(null);
    }
  }, [addRequest, open]);

  return { dialog, open, close };
};

export default useCareerEntries;
